
export type Language = 'en' | 'sv' | 'da';

export interface LanguageOption {
    code: Language;
    name: string;
}

export type Translations = {
    [key in Language]: {
        [key: string]: string;
    };
};

export enum SearchType {
    PackageTours = 'PackageTours',
    OnlyHotel = 'OnlyHotel',
    Transfer = 'Transfer',
}

export interface LocationOption {
    id: string;
    name: string;
    type?: number;
    parentId?: string;
}

export interface DateOption {
    date: string;
}

export interface NightOption {
    night: number;
}

export interface SearchQuery {
    searchId?: string; // Added for context
    type: SearchType;
    departureId: string;
    departureName?: string;
    departureType?: number;
    destinationId: string;
    destinationName?: string;
    destinationType?: number;
    hotelId?: string;
    checkIn: string;
    nights: number;
    rooms: number; // Added Room Count
    nationality: string;
    currency: string;
    culture?: string; // e.g. 'sv-SE', 'da-DK', 'en-US'
    adults: number;
    children: number;
    childrenAges: number[];
}

export interface FlightSearchQuery {
    tripType: 'one-way' | 'round-trip';
    departureAirport: string;
    arrivalAirport: string;
    departureDate: string;
    returnDate?: string;
    adults: number;
    children: number;
    infants: number;
}

export interface FlightOffer {
    id: string;
    airline: string;
    flightNumber: string;
    departureAirport: string;
    arrivalAirport: string;
    departureTime: string;
    arrivalTime: string;
    duration: string;
    price: number;
    currency: string;
    stops: number;
    logoUrl: string;
}

export interface FilterCriteria {
    name?: string;
    priceRange?: [number, number];
    stars?: number[];
    boards?: string[];
    themes?: string[];
    facilities?: string[];
}

export interface FlightSegment {
    airline: string;
    airlineCode?: string;
    flightNumber: string;
    departureAirport: string;
    departureAirportName?: string;
    departureAirportCode?: string;
    arrivalAirport: string;
    arrivalAirportName?: string;
    arrivalAirportCode?: string;
    departureTime: string;
    arrivalTime: string;
    duration: string;
    stops: number;
    logoUrl?: string;
    flightDate?: string; // e.g., "23 Jun 2026"
    baggage?: string; // e.g., "20 kg" or "1 pc"
    cabinClass?: string; // e.g., "Economy", "Business"
    notes?: string; // Special notes from the flight provider
    segments?: FlightSegment[]; // Detailed segments for layovers
    layoverMinutes?: number; // Layover duration in minutes (between this and next segment)
    layoverAirport?: string; // Layover airport name
    layoverAirportCode?: string; // Layover airport code
}

export interface FlightAlternative {
    id: string;
    name: string;
    outbound: FlightSegment;
    return?: FlightSegment;
    priceDifference: number;
    currency: string;
}

export interface RealFlightOption {
    departureTime: string;
    arrivalTime: string;
    departureAirport: string;
    arrivalAirport: string;
    airline: string;
    airlineCode?: string;
    departureDate?: string;
    logoUrl?: string;
    duration?: string;
    flightNumber?: string;
    stops?: number;
    baggage?: string;
    segments?: any[];
}

export interface BrandedFareFeature {
    name: string;
    included: boolean;
    icon: string;
    code?: string;
}

export interface BrandedFare {
    id: string;
    offerId: string;
    name: string;
    features: BrandedFareFeature[];
    price: number;
    currency: string;
    isDefault: boolean;
    order: number;
}

export interface Offer {
    id: string;
    name: string;
    board: string;
    roomType: string;
    price: number;
    currency: string;
    checkIn: string;
    night: number;
    available: boolean;
    outboundFlight?: FlightSegment;
    returnFlight?: FlightSegment;
    outboundBrandedFares?: BrandedFare[];
    returnBrandedFares?: BrandedFare[];
}

export interface HotelImage {
    url: string;
    thumbnailUrl?: string;
    caption?: string;
    isMain?: boolean;
    source?: string;
    isApproved?: boolean;
}

export interface Hotel {
    id: string;
    productId?: string;
    provider?: number;
    name: string;
    location: {
        country: string;
        city: string;
    };
    rating: number;
    pricePerNight: number;
    currency: string;
    imageUrl: string;
    thumbnailUrl?: string;
    board: string;
    roomType: string;
    themes: string[];
    facilities: string[];
    offers: Offer[];
    description?: {
        en?: string;
        sv?: string;
        da?: string;
    };
    shortDescription?: {
        en?: string;
        sv?: string;
        da?: string;
    };
    images?: string[] | HotelImage[];
    isEdited?: boolean;
    instagramUrl?: string;
    swedishDescription?: string;
    lumiCategory?: string;
    lumiConcept?: string;
    destination?: string;
}

export interface StructuredHotelContent {
    intro: string;
    dining: string;
    activities: string;
    rooms: string;
    location: string; // New
    family: string;   // New
    wellness: string; // New
    other: string;
}

export interface HolidayPackage extends Hotel {
    searchId?: string;
    dealDescription: string;
    totalPrice: number;
}

export interface Tour {
    id: string;
    name: string;
    location: string;
    rating: number;
    price: number;
    currency: string;
    imageUrl: string;
    duration: string;
}

export interface ProductInfo {
    description: string;
    images: string[];
    facilities: string[];
    coordinates?: {
        latitude: string;
        longitude: string;
    }
}

export interface ApiSettings {
    serviceAddress: string;
    agency: string;
    user: string;
    password: string;
    token?: string;
    activeProviders?: string[];
}

export interface ApiLogEntry {
    timestamp: string;
    request: any;
    response: string;
    status: 'success' | 'error';
    endpoint: string;
}

export interface ImageProcessingOptions {
    width?: number;
    height?: number;
    mode?: 'pad' | 'boxpad' | 'crop' | 'min' | 'max' | 'stretch';
    anchor?: 'center' | 'top' | 'right' | 'bottom' | 'left' | 'topleft' | 'topright' | 'bottomleft' | 'bottomright';
    bgcolor?: string;
    widthratio?: number;
    heightratio?: number;
    quality?: number;
}

export interface Passenger {
    id: number;
    type: 'Adult' | 'Child';
    firstName: string;
    lastName: string;
    birthDate: string;
    gender: 'Male' | 'Female';
    passportNumber?: string;
    nationality: string;
}

export interface ContactDetails {
    email: string;
    phone: string;
    address: string;
    city: string;
    zipCode: string;
    country: string;
}

export interface BookingRequest {
    hotel: Hotel;
    offer: Offer;
    searchQuery: SearchQuery;
    passengers: Passenger[];
    contact: ContactDetails;
}

export interface CardInfo {
    ExpiryMonth: string;
    ExpiryYear: string;
    CardNo: string;
    CardHolderFirstName: string;
    CardHolderLastName: string;
    CvvNo: string;
}

export interface PaymentDetails {
    cardNumber: string;
    cardHolder: string;
    expiryDate: string;
    cvv: string;
}

export interface ReservationResult {
    reservationNumber: string;
    status: 'Confirmed' | 'Pending' | 'Failed' | 'Cancelled';
    totalPrice: number;
    currency: string;
    hotelName: string;
    checkIn: string;
    checkOut: string;
}

export interface BookingRecord extends ReservationResult {
    id: string;
    bookingDate: string;
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    passengers: Passenger[];
}

export interface FeaturedHotelConfig {
    hotelId: string;
    queryOffsetDays: number;
    queryDuration: number;
}

// Widget Types
export interface Widget {
    id: string;
    type: string;
    order: number;
    settings: Record<string, any>;
    content: Record<string, any>;
    grid: { cols: number; colSpan: number; rowSpan: number };
    visibility: { desktop: boolean; tablet: boolean; mobile: boolean };
}

export interface WeatherData {
    temp: number;
    condition: string;
    water: number;
}

export interface DestinationContent {
    _id?: string; // MongoDB ID
    id: string; // Tourvisio ID or Slug
    destinationId: string;
    slug?: string;
    title: string;
    titleSv?: string;
    imageUrl: string;
    coverImage?: string;
    heroImage?: string;
    heroVideo?: string;
    price: string;
    type: string;
    isActive: boolean;
    isFeatured?: boolean;
    landingPage?: {
        heroImage?: string;
        description: {
            en: string;
            sv: string;
            da?: string;
        };
        featuredHotels?: FeaturedHotelConfig[];
    };
    richContent?: {
        history: {
            en: string;
            sv: string;
            da?: string;
        };
        nature: {
            en: string;
            sv: string;
            da?: string;
        };
    };
    gallery?: string[];

    // New Fields
    layout?: Widget[];
    weather?: {
        [key: string]: WeatherData; // jan, feb, etc.
    };

    // New visitdubai.com-style content sections
    sections?: {
        thingsToDo?: DestinationActivity[];
        attractions?: DestinationAttraction[];
        restaurants?: DestinationRestaurant[];
        itineraries?: DestinationItinerary[];
        practicalInfo?: DestinationPracticalInfo;
    };

    // Tour Search Configuration (admin-editable per destination)
    tourSearchConfig?: {
        checkIn?: string;
        nights?: number;
        adults?: number;
        children?: number;
        departureId?: string;
        departureName?: string;
        currency?: string;
        nationality?: string;
        futureDays?: number;
    };

    // Hotel Whitelist (admin selects which hotels to show)
    hotelWhitelist?: { hotelId: string; name: string; imageUrl?: string }[];

    // CTA Bar config
    ctaBar?: {
        enabled?: boolean;
        hotelName?: string;
        text?: { en?: string; sv?: string; da?: string };
        link?: string;
        hotelId?: string;
    };

    sectionConfig?: {
        sectionId: string;
        visible?: boolean;
        order: number;
    }[];

    // Hybrid Data Architecture fields
    dataSource?: 'seed' | 'manual' | 'tourvisio_api' | 'ai_enriched';
    lockContent?: boolean;

    // Airport & Transportation Info
    airportInfo?: {
        name?: { en?: string; sv?: string; da?: string };
        code?: string;
        content?: { en?: string; sv?: string; da?: string };
        isActive?: boolean;
    };
}

// Destination Activity (Things to Do)
export interface DestinationActivity {
    id: string;
    title: { en: string; sv: string; da?: string };
    description: { en: string; sv: string; da?: string };
    image: string;
    category: 'adventure' | 'culture' | 'nature' | 'family' | 'nightlife' | 'shopping' | 'wellness' | 'food' | 'beach';
    duration?: string;
    price?: string;
    rating?: number;
    aiComment?: { en: string; sv: string; da?: string };
}

// Destination Attraction (Sights & Landmarks)
export interface DestinationAttraction {
    id: string;
    title: { en: string; sv: string; da?: string };
    description: { en: string; sv: string; da?: string };
    image: string;
    category: 'landmark' | 'museum' | 'beach' | 'park' | 'historic' | 'religious' | 'nature' | 'family' | 'scenic' | 'culture' | 'adventure' | 'entertainment';
    address?: string;
    openingHours?: string;
    entryFee?: string;
    aiComment?: { en: string; sv: string; da?: string };
}

// Destination Restaurant
export interface DestinationRestaurant {
    id: string;
    name: string;
    slug?: string;
    description: { en: string; sv: string; da?: string };
    image: string;
    gallery?: string[];
    videoUrl?: string;
    cuisine: string;
    priceRange: '$' | '$$' | '$$$' | '$$$$';
    rating?: number;
    address?: string;
    website?: string;
    phone?: string;
    openingHours?: string;
    reservationEnabled?: boolean;
    aiComment?: { en: string; sv: string; da?: string };
    coordinates?: { lat: number; lng: number };
    mapUrl?: string;
    tripAdvisorUrl?: string;
}

// Destination Itinerary
export interface DestinationItinerary {
    id: string;
    title: { en: string; sv: string; da?: string };
    description: { en: string; sv: string; da?: string };
    image: string;
    days: number;
    highlights: { en: string[]; sv: string[]; da?: string[] };
    aiComment?: { en: string; sv: string; da?: string };
}

// Practical Info
export interface DestinationPracticalInfo {
    currency: { code: string; name: { en: string; sv: string; da?: string } };
    language: { en: string; sv: string; da?: string };
    timezone: string;
    visaInfo: { en: string; sv: string; da?: string };
    visa?: { en: string; sv: string; da?: string };
    health?: { en: string; sv: string; da?: string };
    emergency?: { en: string; sv: string; da?: string };
    electricity?: { en: string; sv: string; da?: string };
    bestTimeToVisit: { en: string; sv: string; da?: string };
    transport: {
        title: { en: string; sv: string; da?: string };
        description: { en: string; sv: string; da?: string };
        icon: string;
    }[];
    tips: { en: string[]; sv: string[]; da?: string[] };
}

export interface Ad {
    id: string;
    _id: string;
    name: string;
    type: 'hotel' | 'tour' | 'package' | 'banner' | 'carousel' | 'video';
    status: 'active' | 'paused' | 'pending_review' | 'rejected' | 'scheduled';
    headline?: string;
    headlineSv?: string;
    description?: string;
    descriptionSv?: string;
    displayUrl?: string;
    destinationUrl?: string;
    callToAction?: string;
    callToActionSv?: string;
    mediaUrl?: string;
    thumbnailUrl?: string;
    additionalImages?: string[];
    hotelId?: string;
    hotelName?: string;
    destinationId?: string;
    destinationName?: string;
    departureCity?: string;
    departureCityId?: string;
    displayPrice?: number;
    originalPrice?: number;
    currency?: string;
    discountPercentage?: number;
    nights?: number;
    board?: string;
    checkInDate?: string;
    showOnHomepage?: boolean;
    showInSearchResults?: boolean;
    showOnDestinationPages?: boolean;
    positionIndex?: number;
    gtagParams?: any;
    trackedUrl?: string;
}

export interface SearchResultCTA extends Ad {
    // Legacy support, Ad covers most fields now
}

export interface WidgetSettings {
    heroTitle1?: string;
    heroTitle2?: string;
    heroSubtitle?: string;
    heroImage?: string;
    campaignText?: string;
    campaignActive?: boolean;
    campaignStartDate?: string;
    campaignEndDate?: string;
    campaignBgColor?: string;
    campaignTextColor?: string;
    ctaTitle?: string;
    ctaDescription?: string;
    ctaHotelId?: string;
    restrictToWhitelist?: boolean;
    // Social Media Links
    facebookUrl?: string;
    instagramUrl?: string;
    linkedinUrl?: string;
    // Flash Sale Settings (controls homepage CTA banner)
    flashSaleActive?: boolean;
    flashSaleImageUrl?: string;
    flashSaleTitle?: { en: string; sv: string };
    flashSaleDescription?: { en: string; sv: string };
    flashSaleLinkUrl?: string;
    flashSaleStartDate?: string;
    flashSaleEndDate?: string;
    flashSaleBgColor?: string;
    flashSaleTextColor?: string;
    flashSaleTargetType?: 'none' | 'hotel' | 'destination';
    flashSaleHotelId?: string;
    flashSaleHotelName?: string;
    flashSaleDestinationId?: string;
    flashSaleDestinationName?: string;
    // Homepage CTA Banner Settings
    ctaBannerActive?: boolean;
    ctaBannerHotelId?: string;
    ctaBannerHotelName?: { en: string; sv: string };
    ctaBannerDeparture?: string;
    ctaBannerDepartureId?: string;
    ctaBannerDestination?: string;
    ctaBannerDestinationId?: string;
    ctaBannerCheckIn?: string;
    ctaBannerNights?: number;
    ctaBannerBoard?: string;
    // Membership Section
    showMembershipSection?: boolean;
    // Google Analytics Settings
    googleAnalyticsId?: string;
    googleAnalyticsEnabled?: boolean;
    cookieConsentEnabled?: boolean;
}

export interface WhitelistItem {
    id: string;
    name: string;
}

export interface MailchimpSettings {
    apiKey: string;
    serverPrefix: string;
    audienceId: string;
    isActive: boolean;
}

export interface DestinationMapping {
    name: string;
    id: string;
}

export interface DashboardChartData {
    labels: string[];
    datasets: {
        label: string;
        data: number[];
        color: string;
    }[];
}

export interface SystemHealth {
    dbStatus: boolean;
    apiStatus: string;
    errorCount: number;
}

export type ChatMessageType = 'text' | 'search_result' | 'search_carousel' | 'booking_form' | 'system';

export interface ChatMessage {
    id: string;
    sender: 'user' | 'ai' | 'admin';
    text: string;
    timestamp: number;
    messageType?: ChatMessageType;
    payload?: any;
}

export interface ChatSession {
    id: string;
    userId: string;
    userName?: string;
    status: 'ai' | 'waiting_for_agent' | 'agent_active' | 'closed';
    messages: ChatMessage[];
    unreadCount: number;
    lastUpdated: number;
    language?: 'en' | 'sv';
}

export interface Traveller {
    travellerId: string;
    type: number;
    title: number;
    name: string;
    surname: string;
    isLeader: boolean;
    birthDate: string;
    nationality: {
        twoLetterCode: string;
    };
    address?: {
        email?: string;
        phone?: string;
        contactPhone?: {
            countryCode?: string;
            areaCode?: string;
            phoneNumber?: string;
        };
    };
    passportInfo?: {
        serial?: string;
        number?: string;
        expireDate?: string;
        issueDate?: string;
        citizenshipCountryCode?: string;
    };
}

export interface SetReservationInfoRequest {
    transactionId: string;
    travellers: Traveller[];
    customerInfo?: any;
    reservationNote?: string;
    agencyReservationNumber?: string;
}

export interface BeginTransactionResponse {
    body: {
        transactionId: string;
        expiresOn: string;
    };
    header: {
        success: boolean;
        messages: any[];
    };
}

export interface PaymentOption {
    id: string;
    name: string;
    thumbnail?: string;
    paymentTypes: PaymentType[];
}

export interface PaymentType {
    id: string;
    name: string;
    paymentCurrency: string;
    secure3D: boolean;
    instalment: number[];
    price: {
        amount: number;
        currency: string;
    };
}

export interface PaymentGroup {
    id: string;
    name: string;
    options: PaymentOption[];
}

export interface BeginPaymentTransactionRequest {
    TransactionId: string;
    PaymentOption: number;
    PaymentTypeId: string;
    Installment: number;
    CardInfo?: CardInfo;
    SuccessUrl?: string;
    ErrorUrl?: string;
    Currency: string;
    Culture: string;
    MakeCommitTransactionAfterPaymentAsync?: boolean;
}

export interface PaymentTransactionResponse {
    body: {
        html?: string;
        postUrl?: string;
        paymentTransactionType: number;
        paymentTransactionId: string;
        responseType?: number;
    };
    header: {
        success: boolean;
        messages: any[];
    };
}

export interface CheckPaymentRequest {
    paymentTransactionId: string;
}

export interface CheckPaymentResponse {
    body: {
        paymentTransactionId: string;
        status: number;
    };
    header: {
        success: boolean;
        messages: any[];
    };
}

export interface GetTransactionStatusRequest {
    transactionId: string;
}

export interface GetTransactionStatusResponse {
    body: {
        processStatus: number;
        status: number;
        transactionType: number;
    };
    header: {
        success: boolean;
        messages: any[];
    };
}

export interface GetCancellationPenaltyRequest {
    reservationNumber: string;
}

export interface CancelPenaltyService {
    provider: number;
    id: string;
    code: string;
    productType: number;
    name: string;
    price: {
        amount: number;
        currency: string;
    };
    isCancelable: boolean;
    priceDetail?: {
        totalSalePrice: { amount: number, currency: string };
        penalty: { amount: number, currency: string };
        mainServiceFee: { amount: number, currency: string };
    };
}

export interface CancelPenalty {
    reason: {
        id: string;
        name: string;
        comment?: string;
    };
    services: CancelPenaltyService[];
    price: {
        amount: number;
        currency: string;
    };
    isCancelable: boolean;
}

export interface GetCancellationPenaltyResponse {
    body: {
        cancelPenalties: CancelPenalty[];
    };
    header: {
        success: boolean;
        messages: any[];
    };
}

export interface CancelReservationRequest {
    reservationNumber: string;
    reason: string;
    serviceIds?: string[];
}

export interface CancelReservationResponse {
    body: {
        reservationStatus: number;
    };
    header: {
        success: boolean;
        messages: any[];
    };
}

export interface GetPaymentTypesRequest {
    culture: string;
    transactionId: string;
}

export interface PaymentTypeLookup {
    paymentTypeName: string;
    bankRecID: number;
    market: string;
    bank: string;
    payCat: number;
    creditCard: string;
    sd: string;
    bankName: string;
    creditCardName: string;
    name: string;
    id: string;
    code: string;
}

export interface LookupCurrency {
    code: string;
    internationalCode: string;
    iconText: string;
    name: string;
}

export interface GetPaymentTypesResponse {
    body: {
        paymentDate: string;
        paymentFrom: number;
        paymentTypes: PaymentTypeLookup[];
        accounts: { name: string }[];
        currencies: LookupCurrency[];
    };
    header: {
        success: boolean;
        messages: any[];
    };
}

export interface GetTransportationsRequest {
    productType: number;
    date: string;
}

export interface TransportationLocation {
    id: string;
    name: string;
}

export interface TransportationAirport {
    name: string;
    id: string;
    code: string;
}

export interface TransportationSegmentPoint {
    date: string;
    location: TransportationLocation;
    airport: TransportationAirport;
}

export interface TransportationDetail {
    productType: number;
    airline: {
        thumbnail: string;
        thumbnailFull: string;
        logo: string;
        logoFull: string;
        id: string;
        name: string;
    };
    departure: TransportationSegmentPoint;
    arrival: TransportationSegmentPoint;
    duration: number;
    selectableRoutes: string[];
    pnlName: string;
    seatInfo: {
        availableSeatCount: number;
        availableSeatCountType: number;
    };
    id: string;
    code: string;
}

export interface TransportationOption {
    night: number;
    transportationClass: { code: string };
    date: string;
    outbound: TransportationDetail;
    return?: TransportationDetail;
}

export interface GetTransportationsResponse {
    body: {
        transportations: TransportationOption[];
    };
    header: {
        success: boolean;
        messages: any[];
    };
}

export interface GetExchangeRatesRequest {
    currency: string;
}

export interface ExchangeRatePair {
    baseCurrency: string;
    counterCurrency: string;
    rate: number;
}

export interface GetExchangeRatesResponse {
    body: {
        pairs: ExchangeRatePair[];
    };
    header: {
        success: boolean;
        messages: any[];
    };
}

export interface TravelMateCard {
    id: number | string;
    badge: string;
    title: string;
    details_summary: string;
    total_price: number;
    why_this_option: string;
    image_url?: string;
    vibe?: string; // New: E.g. "VIBE: MODERN"
    duration?: string; // New: E.g. "5 Days · Shopping & Skyline"
    best_season?: string; // New: E.g. "Best in Nov-Mar"
}

export interface TravelMateSuggestionChip {
    label: string;
    action: string;
    icon?: string;
}

export interface TravelMateResponse {
    assistant_reply: string;
    status: 'success' | 'negotiation_needed' | 'offline' | string;
    budget_analysis?: {
        original_cost: number;
        user_budget: number;
        gap: number;
    };
    route_insight?: {
        vibe: string;
        trend: string;
        local_tip: string;
    };
    interactive_cards?: TravelMateCard[];
    suggestion_chips?: TravelMateSuggestionChip[];
    intent?: 'chat' | 'start_search' | 'select_search_type' | 'select_departure' | 'select_destination' | 'select_date' | 'select_nights' | 'select_travelers' | 'quick_search' | 'search' | 'book_request' | string;
    search_params?: {
        searchType?: string;
        destination?: string;
        departure?: string;
        checkIn?: string;
        nights?: number;
        adults?: number;
        children?: number;
    };
    selected_value?: string;
    booking_url?: string;
}

// Room Scraping Data Types
export interface RoomScrapeData {
    name: string;
    description: string;
    photos: string[];
    bedType?: string;
    size?: string;
}

export interface PagingOption {
    CurrentPage: number;
    PageRowCount: number;
    ProductType: number;
    GetPagingCount: boolean;
    GetFilters?: boolean;
    Filters?: any[];
    IsNewPagingRequest?: boolean;
}

export interface GetPagingDataRequest {
    Currency: string;
    Culture: string;
    SearchId: string;
    PagingOptions: PagingOption[];
}
